import React from 'react';
import {
    SportsHockey as SportsHockeyIcon,
    DirectionsRun as DirectionsRunIcon,
    SportsMotorsports as SportsMotorsportsIcon,
    SportsBaseball as SportsBaseballIcon,
    Home as HomeIcon,
    DirectionsCar as DirectionsCarIcon,
    EmojiEvents as EmojiEventsIcon,
    Celebration as CelebrationIcon,
    Cake as CakeIcon,
    Star as StarIcon,
    Groups as GroupsIcon,
    Event as EventIcon,
} from '@mui/icons-material';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { CalendarEvent, SeasonRoster, TournamentGame, VolunteerDuties } from '../types';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { getRink, getRinkSheet, RINK_DATA, Rink } from './locationUtils';
import { TOURNAMENTS, TournamentInfo } from '../tournaments';

// Enum to define the different types of events
export enum EventType {
    Game,
    Practice,
    GVN,
    Goalie,
    Tournament,
    Floorball,
    Other,
    Holiday,
    SpecialEvent,
    Exhibition,
    Birthday,
    ParentMeeting,
    StickAndPuck,
}

const CENTRAL_TIMEZONE = 'America/Chicago';
export interface EventsByDay {
    [day: string]: CalendarEvent[];
}

/**
 * Formats known location abbreviations into full names.
 * @param location The original location string.
 * @returns A JSX element with a link to Google Maps, or the original string if no location is provided.
 */

/**
 * Parses an event date string or Date object into a Date in Central Time.
 */
export const parseEventDate = (d: Date | string): Date => {
    if (typeof d === 'string') {
        if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/.test(d)) {
            const [datePart, timePart = '00:00:00'] = d.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number);
            return fromZonedTime(new Date(year, month - 1, day, hours, minutes, seconds), CENTRAL_TIMEZONE);
        }
        return new Date(d);
    }
    return d;
};

export const formatEventTime = (event: CalendarEvent, dateType: 'start' | 'end' = 'start'): string => {
    const eventDate = parseEventDate(event[dateType]);
    const rink = getRink(event.location);
    const rinkData = RINK_DATA.get(rink);
    const eventTimezone = rinkData?.timezone || CENTRAL_TIMEZONE;

    if (eventTimezone !== CENTRAL_TIMEZONE) {
        const localTimeWithTz = eventDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: eventTimezone,
            timeZoneName: 'short'
        }).replace(' GMT-5', ' CT').replace(' GMT-4', ' ET'); // More robust replacement might be needed
        
        const centralTime = eventDate.toLocaleTimeString('en-US', {
            hour: 'numeric', minute: '2-digit', hour12: true, timeZone: CENTRAL_TIMEZONE
        });
        return `${localTimeWithTz} (${centralTime} CT)`;
    }

    return eventDate.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true, timeZone: CENTRAL_TIMEZONE
    });
};


/**
 * Returns date in YYYY-MM-DD format using Central Time (America/Chicago) components.
 */
export const getLocalDateString = (d: Date | string): string => {
    if (typeof d === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
            return d;
        }
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(d)) {
            return d.substring(0, 10);
        }
        const dateObj = new Date(d);
        if (!isNaN(dateObj.getTime())) {
            return formatInTimeZone(dateObj, CENTRAL_TIMEZONE, 'yyyy-MM-dd');
        }
        return d.substring(0, 10);
    }
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Determines whether an event is an all-day event.
 */
export const isAllDayEvent = (event: CalendarEvent): boolean => {
    if (event.isAllDay || event.isHoliday) return true;
    if (!event.start) return false;
    const isStartMidnight = !event.start.includes('T') || event.start.includes('T00:00:00');
    const isEndMidnightOrEndOfDay = !event.end || !event.end.includes('T') || event.end.includes('T23:59:59') || event.end.includes('T00:00:00');
    return isStartMidnight && isEndMidnightOrEndOfDay;
};

/**
 * Determines whether an event spans across multiple days.
 */
export const isMultiDayEvent = (event: CalendarEvent): boolean => {
    if (!event.start || !event.end) return false;
    const startDay = getLocalDateString(event.start);
    const endDay = getLocalDateString(event.end);
    if (startDay === endDay) return false;

    // If start and end are date-only strings (or T00:00:00 to T00:00:00) with exactly 1 day difference,
    // this is an RFC 5545 all-day event with exclusive DTEND (1 day duration, not multi-day).
    const isStartMidnightOrDateOnly = !event.start.includes('T') || event.start.includes('T00:00:00');
    const isEndMidnightOrDateOnly = !event.end.includes('T') || event.end.includes('T00:00:00');

    const startTime = new Date(event.start).getTime();
    const endTime = new Date(event.end).getTime();
    const diffHours = (endTime - startTime) / (1000 * 60 * 60);

    if (isStartMidnightOrDateOnly && isEndMidnightOrDateOnly && Math.round(diffHours) === 24) {
        return false;
    }

    return diffHours >= 18;
};

/**
 * Formats the date or date range for an event.
 */
export const formatEventDateRange = (event: CalendarEvent): string => {
    const startDate = parseEventDate(event.start);
    if (!isMultiDayEvent(event)) {
        return startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: CENTRAL_TIMEZONE });
    }
    const endDate = parseEventDate(event.end);
    return `${startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: CENTRAL_TIMEZONE })} – ${endDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: CENTRAL_TIMEZONE })}`;
};

/**
 * Shortens event summaries for compact display in the visual month calendar grid.
 * Examples:
 * - "2014 CSDHL - 2014 CSDHL GVN Performance" -> "GVN Performance"
 * - "2014 CSDHL - 2014 CSDHL Practice" -> "Practice"
 * - "Hawks at 2014 CSDHL" -> "vs Hawks"
 * - "2014 CSDHL at Vikings" -> "@ Vikings"
 * - "2014 CSDHL & 2015 CSDHL Practice" -> "Practice (2014 & 2015)"
 * - "2014 CSDHL - 2014 CSDHL Game vs. Vipers" -> "vs Vipers"
 */
export const getShortenedEventTitle = (summary: string): string => {
    if (!summary) return summary;

    const trimmed = summary.trim();

    // 1. Home game: "X at 2014 CSDHL" -> "vs X"
    const homeMatch = trimmed.match(/^(.+?)\s+at\s+2014\s+CSDHL$/i);
    if (homeMatch) {
        const opponent = homeMatch[1].replace(/^2014\s+CSDHL\s+/i, '').trim();
        return `vs ${opponent}`;
    }

    // 2. Away game: "2014 CSDHL at X" -> "@ X"
    const awayMatch = trimmed.match(/^2014\s+CSDHL\s+at\s+(.+)$/i);
    if (awayMatch) {
        return `@ ${awayMatch[1].trim()}`;
    }

    // 3. Shared ice practices / skills / training / academy: "2015 CSDHL & 2014 CSDHL Jets Academy" -> "Jets Academy (2015 & 2014)"
    const sharedMatch = trimmed.match(/^(\d{4})\s*CSDHL\s*&\s*(\d{4})\s*CSDHL\s+(Practice|Skills|GVN Performance|GVN|Floorball|Goalie|Jets Academy|Academy)(.*)$/i) ||
                        trimmed.match(/^(\d{4})\s*&\s*(\d{4})\s*CSDHL\s+(Practice|Skills|GVN Performance|GVN|Floorball|Goalie|Jets Academy|Academy)(.*)$/i);
    if (sharedMatch) {
        const [, team1, team2, type] = sharedMatch;
        let normalizedType = type;
        if (/^gvn$/i.test(type)) {
            normalizedType = 'GVN Performance';
        } else if (/^jets academy$/i.test(type) || /^academy$/i.test(type)) {
            normalizedType = 'Jets Academy';
        } else {
            normalizedType = type.charAt(0).toUpperCase() + type.slice(1);
        }
        return `${normalizedType} (${team1} & ${team2})`;
    }

    // 4. Exhibition games: "2014 CSDHL - 2014 CSDHL Game vs. Vipers" or "2014 CSDHL Game vs Vipers" -> "vs Vipers"
    const exhibitionMatch = trimmed.match(/^(?:2014\s+CSDHL\s*-\s*2014\s+CSDHL|2014\s+CSDHL)\s+(?:Game|Scrimmage|Exhibition)\s+(?:vs\.?|versus)\s+(.+)$/i) ||
                            trimmed.match(/^(?:2014\s+CSDHL\s*-\s*2014\s+CSDHL|2014\s+CSDHL)\s+(?:vs\.?|versus)\s+(.+)$/i);
    if (exhibitionMatch) {
        return `vs ${exhibitionMatch[1].trim()}`;
    }

    // 5. Clean up redundant "2014 CSDHL - 2014 CSDHL " or "2014 CSDHL - " or "2014 CSDHL " prefixes
    let cleaned = trimmed
        .replace(/^2014\s+CSDHL\s*-\s*2014\s+CSDHL\s+/i, '')
        .replace(/^2014\s+CSDHL\s*-\s*/i, '')
        .replace(/^2014\s+CSDHL\s+/i, '')
        .trim();

    // Standardize "GVN" -> "GVN Performance" if it's just "GVN"
    if (/^gvn$/i.test(cleaned)) {
        return "GVN Performance";
    }

    // 6. Stick & Puck: "Jets Unstructured Peewee Stick & Puck (Optional)" -> "Stick & Puck (Optional)"
    const lowerTrimmed = trimmed.toLowerCase();
    if (lowerTrimmed.includes('stick & puck') || lowerTrimmed.includes('stick and puck') || lowerTrimmed.includes('stick & shoot') || lowerTrimmed.includes('stick and shoot')) {
        const isOptional = lowerTrimmed.includes('(optional)') || lowerTrimmed.includes('optional');
        return isOptional ? 'Stick & Puck (Optional)' : 'Stick & Puck';
    }

    // 7. Birthdays: "First Last's Birthday" or 'First "Nick" Last\'s Birthday' -> 'First Last (Bday)'
    const birthdayMatch = trimmed.match(/^(.+?)(?:'s)?\s+Birthday$/i);
    if (birthdayMatch) {
        const name = birthdayMatch[1].replace(/"[^"]*"\s*/, '').trim();
        return `${name} (Bday)`;
    }

    return cleaned || trimmed;
};

/**
 * Determines the type of an event based on its summary text.
 * @param summary The event's summary/title.
 * @returns The corresponding EventType.
 */
export const getEventType = (event: CalendarEvent): EventType => {
    if (event.eventType && typeof EventType[event.eventType as keyof typeof EventType] === 'number') {
        return EventType[event.eventType as keyof typeof EventType] as unknown as EventType;
    }
    if (event.isHoliday) return EventType.Holiday;
    if (event.isBirthday || (event.summary && event.summary.toLowerCase().includes('birthday'))) return EventType.Birthday;
    if (event.isTournament) return EventType.Tournament;
    const summary = event.summary;
    if (!summary) return EventType.Other;
    const lowerSummary = summary.toLowerCase();

    // 1. Parent/Team meetings
    if (
        lowerSummary.includes('parent meeting') ||
        lowerSummary.includes('parents meeting') ||
        lowerSummary.includes('director/ parent meeting') ||
        lowerSummary.includes('director/parent meeting') ||
        lowerSummary.includes('director meeting') ||
        lowerSummary.includes('team meeting') ||
        lowerSummary.includes('coaches meeting') ||
        lowerSummary.includes('coach meeting') ||
        lowerSummary.includes('player meeting') ||
        lowerSummary.includes('mandatory meeting') ||
        lowerSummary.includes('parent / team meeting') ||
        lowerSummary.includes('meeting')
    ) {
        return EventType.ParentMeeting;
    }

    // 2. Stick & puck
    if (
        lowerSummary.includes('stick & puck') ||
        lowerSummary.includes('stick and puck') ||
        lowerSummary.includes('stick & shoot') ||
        lowerSummary.includes('stick and shoot') ||
        lowerSummary.includes('stick puck')
    ) {
        return EventType.StickAndPuck;
    }

    // 3. Special events
    if (
        lowerSummary.includes('launch day') ||
        lowerSummary.includes('challenge cup') ||
        lowerSummary.includes('special event') ||
        lowerSummary.includes('banquet') ||
        lowerSummary.includes('party') ||
        lowerSummary.includes('photo day') ||
        lowerSummary.includes('picture day') ||
        lowerSummary.includes('fundraiser')
    ) {
        return EventType.SpecialEvent;
    }

    // 4. Practices (including rollover, skills, academy)
    if (
        lowerSummary.includes('practice') ||
        lowerSummary.includes('jets academy') ||
        lowerSummary.includes('academy') ||
        lowerSummary.includes('2014 csdhl skills') ||
        (lowerSummary.includes("2014 csdhl") && lowerSummary.includes("skills")) ||
        lowerSummary.includes('rollover')
    ) return EventType.Practice;

    // 5. Workouts, Goalie, Floorball
    if (lowerSummary.includes('gvn')) return EventType.GVN;
    if (lowerSummary.includes('goalie')) return EventType.Goalie;
    if (lowerSummary.includes('floorball')) return EventType.Floorball;

    // 6. Tournaments (explicit tournament titles or tournament-associated events)
    if (
        lowerSummary.includes('tournament') ||
        lowerSummary.includes('playoff') ||
        lowerSummary.includes('state tournament') ||
        lowerSummary.includes('silver stick') ||
        lowerSummary.includes('hometown cup') ||
        lowerSummary.includes('world invite') ||
        lowerSummary.includes('super series') ||
        lowerSummary.includes('mlk mayhem') ||
        isTournamentEvent(event)
    ) {
        return EventType.Tournament;
    }

    // Check for regular season games
    const isRegularGame = lowerSummary.includes('at 2014 csdhl') || lowerSummary.includes('2014 csdhl at');
    if (isRegularGame) {
        return EventType.Game;
    }

    // Check for exhibition games
    const isExhibitionGame =
        lowerSummary.includes('game') ||
        lowerSummary.includes('exhibition') ||
        lowerSummary.includes('scrimmage') ||
        lowerSummary.includes(' vs.') ||
        lowerSummary.includes(' vs ') ||
        lowerSummary.includes(' versus ');
    if (isExhibitionGame) {
        return EventType.Exhibition;
    }

    return EventType.Other;
};

export const isTournamentEvent = (event: CalendarEvent): boolean => {
    if (!event || !event.start) return false;

    // Explicit tournament flag or multi-day tournament event
    if (event.isTournament === true) return true;
    if (event.isTournament === false) return false;

    // Non-tournament event categories (practices, meetings, clinics, dryland, workouts, etc.) are never tournament events
    const summary = (event.summary || '').toLowerCase().trim();
    if (
        summary.includes('practice') ||
        summary.includes('skills') ||
        summary.includes('academy') ||
        summary.includes('meeting') ||
        summary.includes('photo') ||
        summary.includes('picture') ||
        summary.includes('party') ||
        summary.includes('banquet') ||
        summary.includes('stick') ||
        summary.includes('clinic') ||
        summary.includes('camp') ||
        summary.includes('dryland') ||
        summary.includes('gvn') ||
        summary.includes('workout') ||
        summary.includes('performance') ||
        summary.includes('goalie') ||
        summary.includes('floorball') ||
        summary.includes('rollover')
    ) {
        return false;
    }

    const eventDateStr = getLocalDateString(event.start);

    for (const t of TOURNAMENTS) {
        if (t.startDate && t.endDate) {
            if (eventDateStr >= t.startDate && eventDateStr <= t.endDate) {
                return true;
            }
        }
    }

    return false;
};

/**
 * Determines if a calendar event is a league game by checking if the opponent is in the provided set of league teams.
 * @param event The calendar event to check.
 * @param leagueTeamNames A Set containing the names of all teams in the league.
 * @returns True if the event is a game against a known league opponent, false otherwise.
 */
export const isLeagueGame = (event: CalendarEvent, leagueTeamNames: Set<string>): boolean => {
    const summary = event.summary.toLowerCase();
    const isGameFormat = summary.includes('at 2014 csdhl') || summary.includes('2014 csdhl at');
    if (!isGameFormat) {
        return false;
    }

    // Extract opponent name and check against the set of league teams
    const parts = summary.split(' at ');
    const team1 = parts[0].replace('2014 csdhl', '').trim();
    const team2 = parts[1].replace('2014 csdhl', '').trim();
    const opponent = summary.includes('at 2014 csdhl') ? team1 : team2;

    return Array.from(leagueTeamNames).some(leagueTeam => opponent.includes(leagueTeam.toLowerCase()));
};

/**
 * Determines whether a calendar event is a home game (Chicago Jets host).
 */
export const isHomeGame = (event: CalendarEvent): boolean => {
    if (!event || !event.summary) return false;
    const summary = event.summary.toLowerCase().trim();

    // Explicit regular season / league patterns
    // e.g. "Sabres at 2014 CSDHL", "Sabres at Chicago Jets", "Sabres at Jets"
    if (summary.includes('at 2014 csdhl') || summary.includes('at 2014 jets') || summary.includes('at chicago jets') || summary.includes('at jets')) {
        return true;
    }
    // e.g. "2014 CSDHL at Sabres", "Jets at Sabres", "Chicago Jets at Sabres", "@ Sabres"
    if (summary.includes('2014 csdhl at') || summary.includes('2014 jets at') || summary.includes('chicago jets at') || summary.includes('jets at') || summary.startsWith('@')) {
        return false;
    }

    // Check location if known home rink
    if (event.location) {
        const rink = getRink(event.location);
        if (rink === Rink.JohnnysEast || rink === Rink.JohnnysWest || rink === Rink.BlackhawksIceCenter) {
            return true;
        }
        if (rink !== Rink.Unknown) {
            return false;
        }
    }

    // "vs" exhibition / scrimmage matches default to home if no other indicator
    if (summary.includes('vs.') || summary.includes('vs ') || summary.includes('versus')) {
        return true;
    }

    return false;
};

/**
 * Determines whether a calendar event is an away game (Chicago Jets visit).
 */
export const isAwayGame = (event: CalendarEvent): boolean => {
    if (!event || !event.summary) return false;
    const summary = event.summary.toLowerCase().trim();

    if (summary.includes('2014 csdhl at') || summary.includes('2014 jets at') || summary.includes('chicago jets at') || summary.includes('jets at') || summary.startsWith('@')) {
        return true;
    }
    if (summary.includes('at 2014 csdhl') || summary.includes('at 2014 jets') || summary.includes('at chicago jets') || summary.includes('at jets')) {
        return false;
    }

    // Check location if known away rink
    if (event.location) {
        const rink = getRink(event.location);
        if (rink !== Rink.Unknown && rink !== Rink.JohnnysEast && rink !== Rink.JohnnysWest && rink !== Rink.BlackhawksIceCenter) {
            return true;
        }
    }

    return !isHomeGame(event);
};

/**
 * Groups a list of calendar events by day.
 * The key is the date string in 'YYYY-MM-DD' format.
 * @param events The list of calendar events.
 * @returns An object with days as keys and arrays of events as values.
 */
export const groupEventsByDay = (events: CalendarEvent[]): EventsByDay => {
    return events.reduce((acc, event) => {
        const day = getLocalDateString(event.start); // YYYY-MM-DD in Central Time
        if (!acc[day]) {
            acc[day] = [];
        }
        acc[day].push(event);
        return acc;
    }, {} as EventsByDay);
};

/**
 * Returns the appropriate Material-UI icon for a given event summary.
 * @param summary The event's summary/title.
 * @returns A JSX element for the icon.
 */
export const getEventIcon = (event: CalendarEvent, customStyle?: object) => {
    const iconStyle = { mr: 0.5, fontSize: '1rem', color: 'inherit', flexShrink: 0, ...customStyle };
    if (event.isHoliday && event.icon) {
        return React.createElement(event.icon, {
            sx: {
                ...iconStyle,
                color: event.color || iconStyle.color,
            }
        });
    }
    const eventType = getEventType(event);

    switch (eventType) {
        case EventType.Birthday:
            return <CakeIcon sx={{ ...iconStyle, color: event.color || '#ec4899' }} />;
        case EventType.ParentMeeting:
            return <GroupsIcon sx={iconStyle} />;
        case EventType.Game:
            if (isHomeGame(event)) return <HomeIcon sx={iconStyle} />;
            if (isAwayGame(event)) return <DirectionsCarIcon sx={iconStyle} />;
            return <SportsHockeyIcon sx={iconStyle} />;
        case EventType.Exhibition:
            return <StarIcon sx={iconStyle} />;
        case EventType.Tournament:
            return <EmojiEventsIcon sx={iconStyle} />;
        case EventType.SpecialEvent:
            return <CelebrationIcon sx={iconStyle} />;
        case EventType.Practice:
        case EventType.StickAndPuck:
            return <SportsHockeyIcon sx={iconStyle} />;
        case EventType.GVN:
            return <DirectionsRunIcon sx={iconStyle} />;
        case EventType.Goalie:
            return <SportsMotorsportsIcon sx={iconStyle} />;
        case EventType.Floorball:
            return <SportsBaseballIcon sx={iconStyle} />;
        default:
            return <EventIcon sx={iconStyle} />;
    }
};

/**
 * Generates a LiveBarn link for a given game.
 * @param location The location of the game.
 * @param startTime The start time of the game (ISO string).
 * @returns A JSX element with a link to LiveBarn, a "No LiveBarn" message, or null.
 */
export const getLiveBarnLink = (location: string, startTime: string): React.ReactNode => {
    if (!location) return null;

    const rink = getRink(location);
    const rinkData = RINK_DATA.get(rink);

    // Rink is not in our known list of rinks with LiveBarn info
    if (!rinkData || rinkData.livebarn === undefined) {
        return null;
    }

    // Rink is known to not have LiveBarn
    if (rinkData.livebarn === null) {
        return <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No LiveBarn</Typography>;
    }

    const now = new Date();
    const gameStart = new Date(startTime);

    // Game is "live" from 1 hour before to 1.5 hours after start time.
    const diffInMinutes = (gameStart.getTime() - now.getTime()) / (1000 * 60);
    const isLive = !isNaN(gameStart.getTime()) && diffInMinutes <= 60 && diffInMinutes > -90;
    const labelText = isLive ? 'Watch Live' : 'LiveBarn';

    const matchedSheet = getRinkSheet(location, rink);

    // 1. If a specific sheet was identified, link directly to it
    if (matchedSheet && matchedSheet.livebarn) {
        const finalUrl = isLive ? `${matchedSheet.livebarn}/live` : matchedSheet.livebarn;
        return (
            <Link href={finalUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                {labelText}
            </Link>
        );
    }

    // 2. If the rink has multiple sheets and no sheet was identified, provide links to all sheets
    if (rinkData.sheets && rinkData.sheets.length > 1) {
        const sheetsWithLivebarn = rinkData.sheets.filter(s => !!s.livebarn);
        if (sheetsWithLivebarn.length > 0) {
            return (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        fontSize: '0.875rem'
                    }}
                >
                    <Tooltip title="Sheet not specified in calendar feed – select your sheet">
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: 'inherit' }}>
                            {labelText}:
                        </Typography>
                    </Tooltip>
                    {sheetsWithLivebarn.map((sheet, idx) => {
                        const sheetUrl = isLive ? `${sheet.livebarn}/live` : sheet.livebarn!;
                        const shortName = sheet.displayName.replace(/\s+Rink$/i, '');
                        return (
                            <React.Fragment key={sheet.key}>
                                {idx > 0 && (
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: 'inherit' }}>
                                        |
                                    </Typography>
                                )}
                                <Link
                                    href={sheetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                >
                                    {shortName}
                                </Link>
                            </React.Fragment>
                        );
                    })}
                </Box>
            );
        }
    }

    // 3. Single-sheet rink (or legacy default URL)
    let barnUrl = rinkData.livebarn?.default;
    if (!barnUrl && rinkData.sheets && rinkData.sheets.length === 1 && rinkData.sheets[0].livebarn) {
        barnUrl = rinkData.sheets[0].livebarn;
    }
    if (!barnUrl && typeof rinkData.livebarn === 'object') {
        const values = Object.values(rinkData.livebarn).filter(Boolean);
        if (values.length === 1) {
            barnUrl = values[0];
        }
    }

    if (!barnUrl) return null;

    const finalUrl = isLive ? `${barnUrl}/live` : barnUrl;

    return (
        <Link href={finalUrl} target="_blank" rel="noopener noreferrer" variant="body2">
            {labelText}
        </Link>
    );
};

/**
 * Parses "Sep 14, 2024" and "2:30 PM" into a Date object representing
 * that time in the 'America/Chicago' (Central Time) zone.
 * @param dateStr The date string (e.g., "Sep 14, 2024")
 * @param timeStr The time string (e.g., "2:30 PM")
 * @returns A Date object correctly representing the Central Time moment.
 */
export function createCentralTimeDate(dateStr: string, timeStr: string): Date {
  let year: number;
  let month: number;
  let day: number;

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parts = dateStr.split('-').map(Number);
    year = parts[0];
    month = parts[1] - 1;
    day = parts[2];
  } else {
    // 1. Parse the date string (e.g. "Oct 15, 2026")
    const monthMap: { [key: string]: number } = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const [monthStr, dayStr, yearStr] = dateStr.replace(',', '').split(' ');
    year = parseInt(yearStr, 10);
    month = monthMap[monthStr]; // 0-indexed (Sep -> 8)
    day = parseInt(dayStr, 10);
  }

  // 2. Parse the time string and convert to 24-hour format
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes] = time.split(':').map(Number);

  if (modifier === 'PM' && hours !== 12) {
    hours += 12;
  }
  if (modifier === 'AM' && hours === 12) {
    hours = 0; // Midnight case
  }
  
  const finalDate = fromZonedTime(new Date(year, month, day, hours, minutes, 0), 'America/Chicago');
  
  return finalDate;
}

/**
 * Parses an event summary to detect and handle "rollover" practices.
 * If the summary contains "2014 CSDHL" followed by a time range (e.g., "5:10-6:10pm"),
 * it adjusts the event's start and end times to that specific range.
 * It also adds original event details for reference.
 * @param event The original calendar event.
 * @returns A new event object, potentially with adjusted times and extra properties.
 */
export const parseRolloverPracticeEvent = (event: CalendarEvent): CalendarEvent => {
    const { summary, start, end } = event;
    const teamIdentifier = '2014 CSDHL';

    // Regex to find a time range like "5:10-6:10pm" or "11:30am-12:30pm" associated with our team.
    const teamTimeRegex = new RegExp(`${teamIdentifier}\\s+(\\d{1,2}:\\d{2})\\s*(am|pm)?\\s*-\\s*(\\d{1,2}:\\d{2})\\s*(am|pm)`, 'i');
    const match = summary.match(teamTimeRegex);

    if (!match) {
        return event; // Not a rollover practice we can parse.
    }

    const [, startTimeStr, startAmPm, endTimeStr, endAmPm] = match;

    const dateStr = getLocalDateString(start);
    const [year, month, day] = dateStr.split('-').map(Number);

    let [startHours, startMinutes] = startTimeStr.split(':').map(Number);
    let [endHours, endMinutes] = endTimeStr.split(':').map(Number);

    const endPeriod = endAmPm.toLowerCase();
    let startPeriod = startAmPm ? startAmPm.toLowerCase() : undefined;
    if (!startPeriod) {
        if (endPeriod === 'pm') {
            if (endHours === 12) {
                startPeriod = startHours < 12 ? 'am' : 'pm';
            } else if (startHours <= endHours) {
                startPeriod = 'pm';
            } else {
                startPeriod = 'am';
            }
        } else {
            startPeriod = 'am';
        }
    }

    if (startPeriod === 'pm' && startHours < 12) startHours += 12;
    if (startPeriod === 'am' && startHours === 12) startHours = 0;
    if (endPeriod === 'pm' && endHours < 12) endHours += 12;
    if (endPeriod === 'am' && endHours === 12) endHours = 0;

    try {
        const startDate = fromZonedTime(new Date(year, month - 1, day, startHours, startMinutes, 0), CENTRAL_TIMEZONE);
        let endDate = fromZonedTime(new Date(year, month - 1, day, endHours, endMinutes, 0), CENTRAL_TIMEZONE);
        if (endDate <= startDate) {
            endDate = fromZonedTime(new Date(year, month - 1, day + 1, endHours, endMinutes, 0), CENTRAL_TIMEZONE);
        }

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return event;
        }

        const startIso = formatInTimeZone(startDate, CENTRAL_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX");
        const endIso = formatInTimeZone(endDate, CENTRAL_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX");

        return {
            ...event,
            summary: '2014 CSDHL Practice',
            start: startIso,
            end: endIso,
            isRollover: true,
            originalSummary: event.summary,
            originalStart: event.start,
            originalEnd: event.end || end,
        };
    } catch (e) {
        console.error("Error parsing rollover practice event:", e);
        return event; // Return original event on error
    }
};

export interface ParseTournamentDateOptions {
    tournament?: TournamentInfo | null;
    location?: string;
    tournamentStartDate?: string;
    tournamentEndDate?: string;
}

/**
 * Parses a tournament date/time string into a Date object.
 * Supports:
 * - Regystra 2026 ordinal card format: "Friday 11th 10:35 AM", "Friday 11th 10:35 AM EDT"
 * - Month name format: "Fri Sep 11, 1:40 PM", "Sep 11 1:40 PM"
 * - Slash date format: "Fri 10/17 5:30 PM", "10/17 5:30 PM"
 * - ISO date strings: "2026-09-11T10:35:00"
 * Uses tournament dates & rink timezone when available to construct the precise zoned time.
 */
export const parseTournamentDateTime = (
    dateTimeStr: string,
    options?: ParseTournamentDateOptions
): Date => {
    if (!dateTimeStr || dateTimeStr.toLowerCase().trim() === 'tbd') {
        return new Date('Invalid Date');
    }

    const clean = dateTimeStr.replace(/\s+/g, ' ').trim();

    // Check if ISO or standard date format with hyphen
    if (clean.includes('-')) {
        const directDate = new Date(clean);
        if (!isNaN(directDate.getTime())) {
            return directDate;
        }
    }

    // Resolve base year and month from tournament if available
    const tournament = options?.tournament;
    const startDateStr = tournament?.startDate || options?.tournamentStartDate;
    const endDateStr = tournament?.endDate || options?.tournamentEndDate;

    let baseYear = new Date().getFullYear();
    let baseMonth = new Date().getMonth(); // 0-indexed

    if (startDateStr) {
        const [sYear, sMonth] = startDateStr.split('-').map(Number);
        if (!isNaN(sYear)) baseYear = sYear;
        if (!isNaN(sMonth)) baseMonth = sMonth - 1;
    }

    // Resolve timezone: check rink location first, then tournament location, then default to Central
    let eventTimezone = CENTRAL_TIMEZONE;
    const locationToCheck = options?.location || tournament?.location;
    if (locationToCheck) {
        const rink = getRink(locationToCheck);
        const rinkData = RINK_DATA.get(rink);
        if (rinkData?.timezone) {
            eventTimezone = rinkData.timezone;
        } else if (/Grand Rapids|Detroit|Michigan|\bMI\b/i.test(locationToCheck)) {
            eventTimezone = 'America/Detroit';
        }
    }

    // Strip timezone abbreviations from time string (EDT, EST, CDT, CST, ET, CT)
    const noTz = clean.replace(/\b(EDT|EST|CDT|CST|ET|CT)\b/gi, '').trim();

    // 1. Ordinal format (Regystra 2026): "Friday 11th 10:35 AM" or "Fri 11th 10:35 AM"
    const matchOrdinal = noTz.match(/(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (matchOrdinal) {
        const day = parseInt(matchOrdinal[1], 10);
        let hours = parseInt(matchOrdinal[2], 10);
        const minutes = parseInt(matchOrdinal[3], 10);
        const ampm = matchOrdinal[4].toUpperCase();

        if (ampm === 'PM' && hours !== 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;

        let month = baseMonth;
        let year = baseYear;

        // If day is before start day (e.g. month rollover from Jan 31 to Feb 1)
        if (startDateStr) {
            const startDay = parseInt(startDateStr.split('-')[2], 10);
            if (day < startDay && endDateStr) {
                const [eYear, eMonth] = endDateStr.split('-').map(Number);
                if (!isNaN(eYear)) year = eYear;
                if (!isNaN(eMonth)) month = eMonth - 1;
            }
        }

        return fromZonedTime(new Date(year, month, day, hours, minutes, 0), eventTimezone);
    }

    // 2. Month name format: "Fri Sep 11, 1:40 PM" or "Sep 11 1:40 PM"
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const matchMonthName = noTz.match(/(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*,?\s+)?([a-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (matchMonthName) {
        const mIdx = months.findIndex(m => matchMonthName[1].toLowerCase().startsWith(m));
        const month = mIdx !== -1 ? mIdx : baseMonth;
        const day = parseInt(matchMonthName[2], 10);
        let hours = parseInt(matchMonthName[3], 10);
        const minutes = parseInt(matchMonthName[4], 10);
        const ampm = matchMonthName[5].toUpperCase();

        if (ampm === 'PM' && hours !== 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;

        return fromZonedTime(new Date(baseYear, month, day, hours, minutes, 0), eventTimezone);
    }

    // 3. Slash format: "Fri 10/17 5:30 PM" or "10/17 5:30 PM"
    const matchSlash = noTz.match(/(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*\s+)?(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (matchSlash) {
        const month = parseInt(matchSlash[1], 10) - 1;
        const day = parseInt(matchSlash[2], 10);
        let hours = parseInt(matchSlash[3], 10);
        const minutes = parseInt(matchSlash[4], 10);
        const ampm = matchSlash[5].toUpperCase();

        if (ampm === 'PM' && hours !== 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;

        const year = month >= 7 ? baseYear : baseYear + 1; // Fallback season logic
        return fromZonedTime(new Date(year, month, day, hours, minutes, 0), eventTimezone);
    }

    return new Date('Invalid Date');
};

/**
 * Converts a structured TournamentGame and TournamentInfo into a standard CalendarEvent.
 */
export const convertTournamentGameToCalendarEvent = (
    game: TournamentGame,
    tournament: TournamentInfo
): CalendarEvent => {
    const parsedDate = parseTournamentDateTime(game.dateTime, {
        tournament,
        location: game.location
    });

    const isDateValid = !isNaN(parsedDate.getTime());
    const startIso = isDateValid
        ? parsedDate.toISOString()
        : `${tournament.startDate}T00:00:00`;
    const endIso = isDateValid
        ? new Date(parsedDate.getTime() + 75 * 60 * 1000).toISOString()
        : `${tournament.startDate}T01:15:00`;

    const summary = game.homeTeam && game.visitorTeam
        ? `${game.homeTeam} vs ${game.visitorTeam}`
        : (game.homeTeam || game.visitorTeam || tournament.name);

    return {
        summary,
        start: startIso,
        end: endIso,
        location: game.location || tournament.location,
        url: tournament.path || (tournament.key ? `/tournaments/${tournament.key}` : tournament.url),
        boxscoreUrl: game.boxscoreUrl,
        isTournament: true,
        isMultiDay: false,
        isAllDay: false,
        tournamentName: tournament.name,
        homeTeam: game.homeTeam,
        awayTeam: game.visitorTeam,
    };
};

/**
 * Resolves the next upcoming tournament game for the Jets from a tournament's games list.
 * Finds the earliest unplayed game (scheduled / scores '-'), or the first game.
 */
export const resolveNextTournamentGame = (
    games: TournamentGame[]
): TournamentGame | null => {
    if (!games || games.length === 0) return null;

    const jetsGames = games.filter(g =>
        (g.homeTeam || '').toLowerCase().includes('jets') ||
        (g.visitorTeam || '').toLowerCase().includes('jets')
    );

    if (jetsGames.length === 0) return null;

    const unplayed = jetsGames.find(g =>
        (g.homeScore === '-' && g.visitorScore === '-') ||
        /scheduled/i.test(g.status || '')
    );

    return unplayed || jetsGames[0];
};

/**
 * Determines if an event represents a game/matchup (as opposed to a practice, meeting, clinic, etc.).
 */
export const isGameEvent = (event: CalendarEvent): boolean => {
    if (!event) return false;
    // Multi-day events are tournament or weekend placeholders, not single games
    if (isMultiDayEvent(event)) return false;

    const summary = (event.summary || '').toLowerCase().trim();
    if (
        summary.includes('practice') ||
        summary.includes('skills') ||
        summary.includes('meeting') ||
        summary.includes('photo') ||
        summary.includes('picture') ||
        summary.includes('party') ||
        summary.includes('banquet') ||
        summary.includes('stick') ||
        summary.includes('clinic') ||
        summary.includes('camp') ||
        summary.includes('dryland') ||
        summary.includes('workout') ||
        summary.includes('performance') ||
        summary.includes('floorball') ||
        summary.includes('rollover')
    ) {
        return false;
    }

    const type = getEventType(event);
    if (type === EventType.Game || type === EventType.Exhibition || type === EventType.Tournament) {
        return true;
    }

    // Also check for 'vs' or 'at' matchup patterns
    if (/\s+(?:vs\.?|versus|at|@)\s+/i.test(summary)) {
        return true;
    }

    return false;
};

/**
 * Determines whether an event or event context is expected to take place on an ice sheet
 * (i.e., on-ice practices, skills clinics, and games).
 * Non-ice events like GVN Performance (dryland), floorball, meetings, parties, banquets,
 * birthdays, and holidays do not take place on an ice sheet and should never show "Unknown Sheet".
 */
export const isIceSheetExpected = (
    eventOrContext?: CalendarEvent | string | EventType,
    location?: string
): boolean => {
    // 1. Explicit EventType enum
    if (typeof eventOrContext === 'number') {
        switch (eventOrContext) {
            case EventType.Game:
            case EventType.Exhibition:
            case EventType.Tournament:
            case EventType.Practice:
            case EventType.Goalie:
            case EventType.StickAndPuck:
                return true;
            case EventType.GVN:
            case EventType.Floorball:
            case EventType.ParentMeeting:
            case EventType.Holiday:
            case EventType.Birthday:
            case EventType.SpecialEvent:
            case EventType.Other:
            default:
                return false;
        }
    }

    // 2. CalendarEvent object
    if (typeof eventOrContext === 'object' && eventOrContext !== null) {
        if (eventOrContext.isHoliday || eventOrContext.isBirthday) {
            return false;
        }
        if (isMultiDayEvent(eventOrContext)) {
            return false;
        }

        const summary = (eventOrContext.summary || '').toLowerCase();

        // Explicit off-ice indicators in summary take absolute precedence
        if (
            /\b(?:gvn|performance|dryland|floorball|meeting|photo|picture|party|banquet|dinner|fundraiser|off-ice|off\s+ice|classroom|conference)\b/i.test(
                summary
            )
        ) {
            return false;
        }

        const type = getEventType(eventOrContext);
        if (
            type === EventType.GVN ||
            type === EventType.Floorball ||
            type === EventType.ParentMeeting ||
            type === EventType.Holiday ||
            type === EventType.Birthday ||
            type === EventType.SpecialEvent
        ) {
            return false;
        }

        if (
            type === EventType.Game ||
            type === EventType.Exhibition ||
            type === EventType.Tournament ||
            type === EventType.Practice ||
            type === EventType.Goalie ||
            type === EventType.StickAndPuck
        ) {
            return true;
        }

        if (isGameEvent(eventOrContext)) {
            return true;
        }

        if (
            /\b(?:practice|skills|academy|jets\s+academy|rollover|clinic|camp|stick\s*&\s*puck|stick\s*and\s*puck|skate|on-ice|on\s+ice|scrimmage)\b/i.test(
                summary
            )
        ) {
            return true;
        }

        return false;
    }

    // 3. String summary or description
    if (typeof eventOrContext === 'string') {
        const text = eventOrContext.toLowerCase();
        if (
            /\b(?:gvn|performance|dryland|floorball|meeting|photo|picture|party|banquet|dinner|fundraiser|off-ice|off\s+ice|classroom|conference)\b/i.test(
                text
            )
        ) {
            return false;
        }
        if (
            /\b(?:game|exhibition|tournament|practice|skills|academy|jets\s+academy|rollover|clinic|camp|stick\s*&\s*puck|stick\s*and\s*puck|goalie|scrimmage|vs\.?|at)\b/i.test(
                text
            )
        ) {
            return true;
        }
        return false;
    }

    // 4. Fallback when no event context provided: inspect location text if available
    if (location) {
        const locLower = location.toLowerCase();
        if (
            /\b(?:gvn|performance|dryland|floorball|meeting|off-ice|off\s+ice|gym|weight\s*room|fitness|turf|classroom|conference)\b/i.test(
                locLower
            )
        ) {
            return false;
        }
    }

    // Default to true for backward compatibility (e.g., tournament game location strings in scrapers/tests)
    return true;
};

/**
 * Formats known location abbreviations into full names.
 * For multi-sheet rinks where a specific sheet was not identified, appends "- Unknown Sheet"
 * ONLY if the event expects an ice sheet (on-ice practices and games).
 * Off-ice events (GVN, dryland, floorball, meetings, banquets) display only the facility name.
 *
 * @param location The original location string.
 * @param eventOrContext Optional event, summary, or EventType to determine if an ice sheet is expected.
 * @returns A JSX element with a link to Google Maps, or the original string if no location is provided.
 */
export const formatLocation = (
    location: string,
    eventOrContext?: CalendarEvent | string | EventType
): React.ReactNode => {
    if (!location) {
        return location;
    }

    const rink = getRink(location);
    const rinkData = RINK_DATA.get(rink);

    let url = rinkData?.mapsUrl;
    if (!url && rinkData?.address) {
        url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rinkData.address)}`;
    }

    if (rink !== Rink.Unknown && rinkData) {
        let displayLocation = rinkData.displayName;
        const lowerLocation = location.toLowerCase();

        // Special handling for Johnny's West Annex
        if (rink === Rink.JohnnysWest && lowerLocation.includes('jwt')) {
            displayLocation = "Johnny's West - Annex";
        } else {
            const matchedSheet = getRinkSheet(location, rink);
            if (matchedSheet) {
                displayLocation = `${rinkData.displayName} - ${matchedSheet.displayName}`;
            } else if (rinkData.sheets && rinkData.sheets.length > 1) {
                if (isIceSheetExpected(eventOrContext, location)) {
                    displayLocation = `${rinkData.displayName} - Unknown Sheet`;
                }
            }
        }
        return (
            <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                {displayLocation}
            </Link>
        );
    }

    // Fallback for unknown locations to a generic Google Maps search
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;

    return (
        <Link href={mapsUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
            {location}
        </Link>
    );
};


/**
 * Filters out non-volunteer keys (like 'Home Team', 'Away Team', 'Location', etc.)
 * from a duties dictionary so only true volunteer roles remain.
 */
export const getCleanVolunteerDuties = (duties?: VolunteerDuties): VolunteerDuties => {
    if (!duties) return {};
    const excludedKeys = [
        'home team', 'away team', 'home', 'away', 'visitor', 'visitor team',
        'opponent', 'team', 'date', 'time', 'location', 'event', 'status'
    ];
    const clean: VolunteerDuties = {};
    for (const [key, value] of Object.entries(duties)) {
        const normKey = key.trim().toLowerCase();
        if (!excludedKeys.includes(normKey) && typeof value === 'string' && value.trim()) {
            clean[key.trim()] = value.trim();
        }
    }
    return clean;
};

/**
 * Extracts and formats the Home and Away team names for a game event.
 * Resolves teams from explicit properties, volunteer sheet metadata, or the event summary.
 */
export const getGameTeams = (event: CalendarEvent): { homeTeam: string; awayTeam: string } | null => {
    if (!event) return null;

    // 1. Direct properties on event
    if (event.homeTeam && event.awayTeam) {
        return { homeTeam: event.homeTeam, awayTeam: event.awayTeam };
    }

    // 2. Check if event.duties contains home/away team fields
    if (event.duties) {
        let home: string | undefined;
        let away: string | undefined;
        for (const [key, value] of Object.entries(event.duties)) {
            const norm = key.trim().toLowerCase();
            if ((norm === 'home team' || norm === 'home') && typeof value === 'string' && value.trim()) {
                home = value.trim();
            } else if ((norm === 'away team' || norm === 'away' || norm === 'visitor' || norm === 'visitor team') && typeof value === 'string' && value.trim()) {
                away = value.trim();
            }
        }
        if (home && away) {
            return { homeTeam: home, awayTeam: away };
        }
    }

    // 3. Only game events should have home and away teams
    if (!isGameEvent(event)) return null;

    // 4. Parse from summary
    const summary = (event.summary || '').trim();
    if (!summary) return null;

    // Pattern: "@ TeamB"
    const atPrefixMatch = summary.match(/^@\s+(.*)$/i);
    if (atPrefixMatch) {
        return { homeTeam: atPrefixMatch[1].trim(), awayTeam: 'Chicago Jets' };
    }

    // Pattern: "TeamA at TeamB"
    const atMatch = summary.match(/^(.*?)\s+at\s+(.*)$/i);
    if (atMatch) {
        let away = atMatch[1].trim();
        let home = atMatch[2].trim();
        if (/^(?:2014\s+csdhl|csdhl)$/i.test(away)) away = 'Chicago Jets';
        if (/^(?:2014\s+csdhl|csdhl)$/i.test(home)) home = 'Chicago Jets';
        return { homeTeam: home, awayTeam: away };
    }

    // Pattern: "TeamA vs. TeamB" or "TeamA vs TeamB" or "TeamA versus TeamB"
    const vsMatch = summary.match(/^(.*?)\s+(?:vs\.?|versus)\s+(.*)$/i);
    if (vsMatch) {
        let teamA = vsMatch[1].trim();
        let teamB = vsMatch[2].trim();
        if (/^(?:2014\s+csdhl|csdhl)$/i.test(teamA)) teamA = 'Chicago Jets';
        if (/^(?:2014\s+csdhl|csdhl)$/i.test(teamB)) teamB = 'Chicago Jets';

        if (isHomeGame(event)) {
            const isJetsB = /\b(?:jets|csdhl)\b/i.test(teamB);
            if (isJetsB) {
                return { homeTeam: teamB, awayTeam: teamA };
            }
            return { homeTeam: teamA, awayTeam: teamB };
        } else {
            const isJetsA = /\b(?:jets|csdhl)\b/i.test(teamA);
            if (isJetsA) {
                return { homeTeam: teamB, awayTeam: teamA };
            }
            return { homeTeam: teamA, awayTeam: teamB };
        }
    }

    return null;
};

/**
 * Extracts and normalizes the opponent name from a game summary.
 * For example:
 * - "2014 CSDHL at Traverse City North Stars" -> "traverse city north stars"
 * - "Traverse City North Stars vs '14 Chicago Jets" -> "traverse city north stars"
 * - "2014 Northstars at 2014 CSDHL" -> "northstars"
 * - "'14 Chicago Jets vs Hartland Hockey Club Black" -> "hartland black"
 */
export const extractOpponentName = (summary: string): string => {
    if (!summary) return '';
    const cleanStr = summary.trim();

    // Check for delimiter ('at', 'vs', 'versus', '@')
    const match = cleanStr.match(/^(.*?)\s+(?:vs\.?|versus|at|@)\s+(.*)$/i);
    let opponentRaw = '';

    if (match) {
        const teamA = match[1].trim();
        const teamB = match[2].trim();

        // Pattern matching Chicago Jets / CSDHL team identity
        const isJetsPattern = /\b(?:2014\s+csdhl|csdhl|chicago\s+jets|'14\s+chicago\s+jets|'14\s+jets|jets\s+2014|jets)\b/i;

        const aIsJets = isJetsPattern.test(teamA);
        const bIsJets = isJetsPattern.test(teamB);

        if (aIsJets && !bIsJets) {
            opponentRaw = teamB;
        } else if (bIsJets && !aIsJets) {
            opponentRaw = teamA;
        } else if (aIsJets && bIsJets) {
            // Both matched (e.g. "2014 Northstars at 2014 CSDHL")
            if (/\b(?:csdhl|jets)\b/i.test(teamB) && !/\b(?:csdhl|jets)\b/i.test(teamA)) {
                opponentRaw = teamA;
            } else {
                opponentRaw = teamB;
            }
        } else {
            opponentRaw = teamA;
        }
    } else {
        opponentRaw = cleanStr;
    }

    // Normalize opponent
    let cleaned = opponentRaw.toLowerCase();
    cleaned = cleaned.replace(/\b(?:2014\s+csdhl|csdhl|chicago\s+jets|'14\s+chicago\s+jets|'14\s+jets|jets\s+2014|jets)\b/g, ' ');
    cleaned = cleaned.replace(/\b20\d\d\b/g, ' ');
    cleaned = cleaned.replace(/\b(?:peewee\s+major|peewee\s+minor|peewee|bantam|squirt|midget)\b/g, ' ');
    cleaned = cleaned.replace(/\b(?:hockey\s+club|hockey|club)\b/g, ' ');
    cleaned = cleaned.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

    return cleaned;
};

/**
 * Checks whether two extracted opponent strings refer to the same team.
 */
export const doOpponentsMatch = (opp1: string, opp2: string): boolean => {
    if (!opp1 || !opp2) return false;
    const o1 = opp1.trim().toLowerCase();
    const o2 = opp2.trim().toLowerCase();

    if (o1 === o2) return true;

    // Substring containment with minimum length check
    if (o1.length >= 4 && o2.length >= 4) {
        if (o1.includes(o2) || o2.includes(o1)) return true;
    }

    // Token overlap comparison
    const tokens1 = o1.split(' ').filter(w => w.length > 2);
    const tokens2 = o2.split(' ').filter(w => w.length > 2);

    if (tokens1.length > 0 && tokens2.length > 0) {
        const commonTokens = tokens1.filter(t => tokens2.includes(t));
        if (commonTokens.length >= 1) {
            const ratio1 = commonTokens.length / tokens1.length;
            const ratio2 = commonTokens.length / tokens2.length;
            if (ratio1 >= 0.5 || ratio2 >= 0.5) {
                return true;
            }
        }
    }

    return false;
};

/**
 * Determines whether two calendar events represent the same real-world game or event.
 * Ensures duplicate representations of the same game (such as an iCal team calendar game
 * and a scraped tournament game) are merged, while preserving distinct concurrent/overlapping
 * events (such as a Game and a Parent Meeting, or doubleheader games with different opponents).
 */
export const isSameGameOrEvent = (event1: CalendarEvent, event2: CalendarEvent): boolean => {
    if (!event1 || !event2) return false;
    if (event1 === event2) return true;

    const s1 = (event1.summary || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const s2 = (event2.summary || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const start1 = event1.start ? event1.start.trim() : '';
    const start2 = event2.start ? event2.start.trim() : '';

    // 1. Exact summary and start time match
    if (start1 === start2 && s1 === s2) {
        return true;
    }

    // 2. Only games can match across different summaries
    if (!isGameEvent(event1) || !isGameEvent(event2)) {
        return false;
    }

    const t1 = new Date(event1.start).getTime();
    const t2 = new Date(event2.start).getTime();
    if (isNaN(t1) || isNaN(t2)) {
        return false;
    }

    const diffMinutes = Math.abs(t1 - t2) / (60 * 1000);

    const opp1 = extractOpponentName(event1.summary);
    const opp2 = extractOpponentName(event2.summary);

    // If both opponents were extracted and match
    if (opp1 && opp2 && doOpponentsMatch(opp1, opp2)) {
        // Same opponent within 180 minutes on the same day/adjacent days
        if (diffMinutes <= 180) {
            return true;
        }
    }

    // If start times are identical or nearly identical (within 15 minutes)
    if (diffMinutes <= 15) {
        // If both opponents were extracted and clearly contradict each other, do not match
        if (opp1 && opp2 && !doOpponentsMatch(opp1, opp2) && opp1.length >= 4 && opp2.length >= 4) {
            if (!opp1.includes(opp2) && !opp2.includes(opp1)) {
                return false;
            }
        }
        return true;
    }

    // If start times are within 45 minutes and venues match
    if (diffMinutes <= 45) {
        const loc1 = (event1.location || '').toLowerCase().trim();
        const loc2 = (event2.location || '').toLowerCase().trim();
        if (loc1 && loc2) {
            const venue1 = loc1.split('-')[0].trim();
            const venue2 = loc2.split('-')[0].trim();
            if (venue1 && venue2 && (venue1.includes(venue2) || venue2.includes(venue1))) {
                return true;
            }
        }
    }

    return false;
};

/**
 * Merges scraped tournament games with calendar events:
 * 1. Removes generic multi-day or tournament placeholder event(s).
 * 2. Matches scraped tournament games with individual calendar game events,
 *    preferring the tournament game's metadata (chip, URL, official matchup format)
 *    while preserving any volunteer duties and location details from the calendar event.
 * 3. Removes duplicate calendar game entries so games are not displayed twice.
 * 4. Preserves all distinct non-game events (practices, meetings, etc.) and non-matching games.
 */
export const mergeTournamentAndCalendarEvents = (
    allEvents: CalendarEvent[],
    jetsTournamentGames: CalendarEvent[],
    targetTournament?: TournamentInfo | null,
    options?: { keepMultiDayPlaceholder?: boolean }
): CalendarEvent[] => {
    if (!allEvents || allEvents.length === 0) {
        return jetsTournamentGames || [];
    }

    if (!targetTournament || !jetsTournamentGames || jetsTournamentGames.length === 0) {
        return [...allEvents];
    }

    const tKey = targetTournament.key;
    const tName = targetTournament.name.toLowerCase().trim();
    const tStart = targetTournament.startDate;

    const isGenericTournamentPlaceholder = (event: CalendarEvent): boolean => {
        // If keepMultiDayPlaceholder is set, preserve multi-day tournament indicators so they display as weekend banners on the calendar
        if (options?.keepMultiDayPlaceholder && isMultiDayEvent(event)) {
            return false;
        }

        const summary = (event.summary || '').toLowerCase().trim();
        const isUrlMatch = !!(event.url && event.url.includes(tKey));
        const isNameMatch = summary.length >= 3 && (summary.includes(tName) || tName.includes(summary));
        const isDateMatch = !!(event.start && event.start.startsWith(tStart));

        // If event has matchup words ('vs', 'at', '@', etc.), it's a specific game, not a generic placeholder
        const isMatchup = /\s+(?:vs\.?|versus|at|@)\s+/i.test(summary);
        if (isMatchup) return false;

        const isTournamentLike = isMultiDayEvent(event) || isAllDayEvent(event) || Boolean(event.isTournament);
        const isTournamentMatch = isNameMatch || isUrlMatch;
        return (isTournamentLike && isTournamentMatch) || (isNameMatch && isDateMatch);
    };

    const enrichedTournamentGames = jetsTournamentGames.map(g => ({ ...g }));
    const matchedCalendarIndices = new Set<number>();

    // For each tournament game, find the matching calendar event (if any)
    for (const tGame of enrichedTournamentGames) {
        let bestMatchIndex = -1;
        let smallestDiff = Infinity;

        for (let i = 0; i < allEvents.length; i++) {
            if (matchedCalendarIndices.has(i)) continue;
            const calEvent = allEvents[i];
            if (isGenericTournamentPlaceholder(calEvent) || isMultiDayEvent(calEvent)) continue;

            if (isSameGameOrEvent(tGame, calEvent)) {
                const tTime = new Date(tGame.start).getTime();
                const calTime = new Date(calEvent.start).getTime();
                const diff = isNaN(tTime) || isNaN(calTime) ? 0 : Math.abs(tTime - calTime);
                if (diff < smallestDiff) {
                    smallestDiff = diff;
                    bestMatchIndex = i;
                }
            }
        }

        if (bestMatchIndex !== -1) {
            matchedCalendarIndices.add(bestMatchIndex);
            const calEvent = allEvents[bestMatchIndex];

            // Preserve volunteer duties from the calendar event
            if (calEvent.duties && Object.keys(calEvent.duties).length > 0) {
                tGame.duties = { ...(calEvent.duties || {}), ...(tGame.duties || {}) };
            }

            // Adopt location if missing on tournament game or if calEvent has sheet and tGame doesn't
            const calRink = getRink(calEvent.location);
            const calSheet = getRinkSheet(calEvent.location, calRink);
            const tRink = getRink(tGame.location);
            const tSheet = getRinkSheet(tGame.location, tRink);

            if (!tGame.location && calEvent.location) {
                tGame.location = calEvent.location;
            } else if (!tSheet && calSheet && calEvent.location) {
                tGame.location = calEvent.location;
            }
        }
    }

    // Keep all calendar events that are neither generic placeholders nor matched duplicates
    const remainingCalendarEvents = allEvents.filter((event, idx) => {
        if (isGenericTournamentPlaceholder(event)) return false;
        if (matchedCalendarIndices.has(idx)) return false;
        return true;
    });

    const combined = [...remainingCalendarEvents, ...enrichedTournamentGames];
    combined.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    return combined;
};

/**
 * Enriches calendar events with missing data (e.g. sheet, tournament URL, boxscore URL, tournament name)
 * from scraped tournament games when a calendar event represents the same game.
 */
export const enrichCalendarEventsWithTournaments = (
    calendarEvents: CalendarEvent[],
    tournamentGames: (CalendarEvent | TournamentGame)[],
    tournament?: TournamentInfo | null
): CalendarEvent[] => {
    if (!calendarEvents || calendarEvents.length === 0) return [];
    if (!tournamentGames || tournamentGames.length === 0) return [...calendarEvents];

    // Convert any raw TournamentGame objects into CalendarEvent format for uniform matching
    const standardTournamentGames: CalendarEvent[] = tournamentGames.map(g => {
        if ('dateTime' in g && 'homeTeam' in g) {
            return convertTournamentGameToCalendarEvent(g, tournament || {
                key: 'tournament',
                name: 'Tournament',
                dates: '',
                startDate: '',
                endDate: '',
                location: g.location || '',
            });
        }
        return g as CalendarEvent;
    });

    return calendarEvents.map(calEvent => {
        if (!isGameEvent(calEvent)) return calEvent;

        const matchingTGame = standardTournamentGames.find(tGame => isSameGameOrEvent(calEvent, tGame));
        if (!matchingTGame) return calEvent;

        const enriched: CalendarEvent = { ...calEvent };

        // 1. Fill in missing sheet / location data if tournament game has it and calEvent doesn't
        const calRink = getRink(calEvent.location);
        const calSheet = getRinkSheet(calEvent.location, calRink);
        const tRink = getRink(matchingTGame.location);
        const tSheet = getRinkSheet(matchingTGame.location, tRink);

        if (!calSheet && tSheet && tRink !== Rink.Unknown) {
            const rinkData = RINK_DATA.get(tRink);
            if (rinkData) {
                enriched.location = `${rinkData.displayName} - ${tSheet.displayName}`;
            } else if (matchingTGame.location) {
                enriched.location = matchingTGame.location;
            }
        } else if (!enriched.location && matchingTGame.location) {
            enriched.location = matchingTGame.location;
        }

        // 2. Fill in tournament metadata
        enriched.isTournament = true;
        if (!enriched.tournamentName && (matchingTGame.tournamentName || tournament?.name)) {
            enriched.tournamentName = matchingTGame.tournamentName || tournament?.name;
        }
        if (!enriched.url && (matchingTGame.url || tournament?.path || tournament?.url)) {
            enriched.url = matchingTGame.url || tournament?.path || tournament?.url;
        }
        if (!enriched.boxscoreUrl && matchingTGame.boxscoreUrl) {
            enriched.boxscoreUrl = matchingTGame.boxscoreUrl;
        }
        if (!enriched.homeTeam && matchingTGame.homeTeam) {
            enriched.homeTeam = matchingTGame.homeTeam;
        }
        if (!enriched.awayTeam && matchingTGame.awayTeam) {
            enriched.awayTeam = matchingTGame.awayTeam;
        }

        // 3. Preserve volunteer duties
        if (calEvent.duties && Object.keys(calEvent.duties).length > 0) {
            enriched.duties = { ...calEvent.duties };
        } else if (matchingTGame.duties && Object.keys(matchingTGame.duties).length > 0) {
            enriched.duties = { ...matchingTGame.duties };
        }

        return enriched;
    });
};

/**
 * Deduplicates events that represent the same game or event.
 * Identical events (same start and summary) and equivalent representations of the same
 * game (e.g. calendar game vs tournament game) are merged, while distinct concurrent
 * events (e.g. Practice and Parent Meeting, or doubleheader games with different opponents)
 * are preserved as distinct events.
 */
export const deduplicateEvents = (events: CalendarEvent[]): CalendarEvent[] => {
    if (!events || events.length === 0) return [];

    const result: CalendarEvent[] = [];

    for (const event of events) {
        if (!event) continue;
        const existingIdx = result.findIndex(e => isSameGameOrEvent(e, event));
        if (existingIdx !== -1) {
            const existing = result[existingIdx];
            if (event.duties && (!existing.duties || Object.keys(existing.duties).length === 0)) {
                existing.duties = { ...event.duties };
            }

            // Sheet enrichment: adopt sheet if existing lacks it and incoming has it
            const existingRink = getRink(existing.location);
            const existingSheet = getRinkSheet(existing.location, existingRink);
            const incomingRink = getRink(event.location);
            const incomingSheet = getRinkSheet(event.location, incomingRink);

            if (!existingSheet && incomingSheet) {
                existing.location = event.location;
            } else if (!existing.location && event.location) {
                existing.location = event.location;
            }

            if (!existing.tournamentName && event.tournamentName) {
                result[existingIdx] = {
                    ...event,
                    duties: existing.duties || event.duties,
                    location: existing.location || event.location,
                };
            }
            continue;
        }

        result.push(event);
    }

    return result;
};

/**
 * Determines the cluster of "next" events from a chronologically sorted list of events.
 * If an event immediately following an event starts within `maxGapMinutes` (default 60 mins)
 * of the previous event's end time (or is concurrent/overlapping with active events in the cluster),
 * both events are included in the next events cluster.
 *
 * @param events Chronologically sorted array of calendar events.
 * @param maxGapMinutes Maximum allowable gap in minutes between the end of one event and the start of the next (default 60).
 * @returns Array of calendar events to display as "Next Event(s)".
 */
export const getNextEvents = (events: CalendarEvent[], maxGapMinutes: number = 60): CalendarEvent[] => {
    if (!events || events.length === 0) {
        return [];
    }

    const dedupedEvents = deduplicateEvents(events);
    if (dedupedEvents.length === 0) return [];

    const nextEvents: CalendarEvent[] = [dedupedEvents[0]];
    const firstStart = new Date(dedupedEvents[0].start).getTime();
    const firstEnd = dedupedEvents[0].end ? new Date(dedupedEvents[0].end).getTime() : firstStart;
    let clusterMinStart = isNaN(firstStart) ? 0 : firstStart;
    let clusterMaxEnd = isNaN(firstEnd) ? clusterMinStart : Math.max(clusterMinStart, firstEnd);

    for (let i = 1; i < dedupedEvents.length; i++) {
        const currEvent = dedupedEvents[i];

        // Multi-day events represent broad date ranges and should not be chained into a back-to-back cluster
        if (isMultiDayEvent(dedupedEvents[0]) || isMultiDayEvent(currEvent)) {
            break;
        }

        const currStart = new Date(currEvent.start).getTime();
        const currEnd = currEvent.end ? new Date(currEvent.end).getTime() : currStart;

        if (isNaN(currStart)) {
            break;
        }

        // Check if currEvent is the SAME game as an event already in nextEvents
        const duplicateIndex = nextEvents.findIndex(e => isSameGameOrEvent(e, currEvent));
        if (duplicateIndex !== -1) {
            const existing = nextEvents[duplicateIndex];
            if (currEvent.duties && (!existing.duties || Object.keys(existing.duties).length === 0)) {
                existing.duties = { ...currEvent.duties };
            }
            const existingRink = getRink(existing.location);
            const existingSheet = getRinkSheet(existing.location, existingRink);
            const incomingRink = getRink(currEvent.location);
            const incomingSheet = getRinkSheet(currEvent.location, incomingRink);
            if (!existingSheet && incomingSheet) {
                existing.location = currEvent.location;
            } else if (!existing.location && currEvent.location) {
                existing.location = currEvent.location;
            }
            continue;
        }

        const maxGapMs = maxGapMinutes * 60 * 1000;
        const gapMs = currStart - clusterMaxEnd;

        // Current event is considered "close" or concurrent if it starts at or after cluster start
        // and starts within maxGapMinutes after the cluster's current maximum end time.
        if (currStart >= clusterMinStart && gapMs <= maxGapMs) {
            nextEvents.push(currEvent);
            const effectiveCurrEnd = isNaN(currEnd) ? currStart : currEnd;
            if (effectiveCurrEnd > clusterMaxEnd) {
                clusterMaxEnd = effectiveCurrEnd;
            }
        } else {
            break;
        }
    }

    return nextEvents;
};

export interface BirthdayOptions {
    seasonStart?: string; // YYYY-MM-DD, default '2025-09-01'
    seasonEnd?: string;   // YYYY-MM-DD, default '2026-03-08'
    startYear?: number;   // default 2025
}

/**
 * Generates birthday CalendarEvent objects for players on a team roster within the regular season bounds.
 * Regular season for 2025-2026 runs from September 1, 2025 through March 8, 2026 (AHAI State Tournament end).
 * Birthdays outside these bounds (e.g. after March 8, 2026 or during summer off-season) are excluded.
 * 
 * @param roster The season roster containing players with DOB.
 * @param options Optional configuration for season boundaries or years array.
 * @returns Array of CalendarEvent objects representing player birthdays within the regular season.
 */
export const getPlayerBirthdayEvents = (
    roster: SeasonRoster | null,
    options?: BirthdayOptions | number[]
): CalendarEvent[] => {
    if (!roster || !roster.players) return [];

    let startYear = 2025;
    if (roster.seasonKey) {
        const match = roster.seasonKey.match(/_(\d{2})_\d{2}$/);
        if (match) {
            startYear = 2000 + parseInt(match[1], 10);
        }
    }

    let seasonStart = `${startYear}-09-01`;
    let seasonEnd = `${startYear + 1}-03-08`;

    if (options) {
        if (Array.isArray(options)) {
            if (options.length === 1) {
                startYear = options[0];
                seasonStart = `${startYear}-09-01`;
                seasonEnd = `${startYear + 1}-03-08`;
            }
        } else {
            if (options.startYear) {
                startYear = options.startYear;
                seasonStart = `${startYear}-09-01`;
                seasonEnd = `${startYear + 1}-03-08`;
            }
            if (options.seasonStart) seasonStart = options.seasonStart;
            if (options.seasonEnd) seasonEnd = options.seasonEnd;
        }
    }

    const events: CalendarEvent[] = [];
    const seen = new Set<string>();

    for (const player of roster.players) {
        if (!player.dob) continue;

        let month: number | null = null;
        let day: number | null = null;

        if (/^\d{4}-\d{2}-\d{2}/.test(player.dob)) {
            const parts = player.dob.substring(0, 10).split('-');
            month = parseInt(parts[1], 10);
            day = parseInt(parts[2], 10);
        } else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(player.dob)) {
            const parts = player.dob.split('/');
            month = parseInt(parts[0], 10);
            day = parseInt(parts[1], 10);
        } else {
            const d = new Date(player.dob);
            if (!isNaN(d.getTime())) {
                month = d.getUTCMonth() + 1;
                day = d.getUTCDate();
            }
        }

        if (!month || !day || isNaN(month) || isNaN(day)) continue;

        // Determine birthday year within the season:
        // Fall/early winter months (Sep-Dec) fall in startYear (e.g. 2025)
        // Late winter/early spring months (Jan-Mar) fall in startYear + 1 (e.g. 2026)
        let eventYear: number | null = null;
        if (month >= 9) {
            eventYear = startYear;
        } else if (month <= 3) {
            eventYear = startYear + 1;
        } else {
            // Months 4-8 (April through August) fall in off-season (after March 8 cutoff and before September start)
            continue;
        }

        const monthStr = String(month).padStart(2, '0');
        const dayStr = String(day).padStart(2, '0');
        const dateStr = `${eventYear}-${monthStr}-${dayStr}`;

        // Strictly enforce season boundaries (e.g. 2025-09-01 through 2026-03-08)
        if (dateStr < seasonStart || dateStr > seasonEnd) {
            continue;
        }

        const displayName = player.nickname
            ? `${player.firstName} "${player.nickname}" ${player.lastName}`
            : `${player.firstName} ${player.lastName}`;

        const summary = `${displayName}'s Birthday`;
        const key = `${summary}-${dateStr}`;

        if (seen.has(key)) continue;
        seen.add(key);

        events.push({
            summary,
            start: `${dateStr}T00:00:00`,
            end: `${dateStr}T23:59:59`,
            location: 'Birthday',
            isBirthday: true,
            isAllDay: true,
            color: '#ec4899',
            icon: CakeIcon,
        });
    }

    return events;
};